/**
 * @description Dedicated resume dialog: Continue (forgive) vs Continue + bill missed.
 *
 * Nested inside RecurringPlanDetail's parent Dialog so Headless UI tracks it.
 * Dead/inactive cards disable both actions. Same-day pause (no open periods)
 * only offers Continue. Header uses an X dismiss control (edit-plan pattern);
 * there is no footer Cancel.
 *
 * @dependencies @headlessui/react, @heroicons/react, lucide-react, recurringPlanService
 * @notes Open-period amountCents may arrive as a string from pg. Coerce with
 *   Number() before summing — `0 + "2000" + "2000"` concatenates.
 */

import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AlertTriangle } from 'lucide-react';
import recurringPlanService from '../../services/api/recurringPlanService';
import { formatCurrency } from '../../utils/invoiceHelpers';
import formatChargeDate from '../../utils/formatChargeDate';
import { getApiErrorMessages } from '../../utils/apiErrorMessage';

export default function ResumeRecurringPlanModal({ plan, onClose, onResumed }) {
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);

  const openPeriods = plan.openPeriods || [];
  const openTotalCents = openPeriods.reduce(
    (sum, row) => sum + (Number(row.amountCents) || 0),
    0
  );
  const openTotalLabel = formatCurrency(openTotalCents / 100);
  const cardUsable = plan.cardUsable === true;
  const nextFuture = plan.nextFutureChargeDate;
  const canBill = cardUsable && openPeriods.length > 0;
  const hasOpenPeriods = openPeriods.length > 0;

  const runResume = async (mode) => {
    try {
      setSubmitting(mode);
      setError(null);
      await recurringPlanService.resumePlan(plan.id, { mode });
      onResumed();
    } catch (err) {
      setError(getApiErrorMessages(err, 'Failed to resume plan').join(' '));
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={submitting ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-lg">
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    Resume plan
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={!!submitting}
                    aria-label="Close"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {error && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {error}
                    </div>
                  )}

                  {!cardUsable && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Attach a live card on Edit Plan (add a card on the customer profile first) before resuming.
                      </span>
                    </div>
                  )}

                  {hasOpenPeriods ? (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Open periods ({openPeriods.length}) · {openTotalLabel}
                      </p>
                      <div className="space-y-1">
                        {openPeriods.map((row) => (
                          <div
                            key={row.date}
                            className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm"
                          >
                            <span className="text-gray-600">
                              {formatChargeDate(row.date)}{' '}
                              <span className="text-red-600">(Failed)</span>
                            </span>
                            <span className="font-medium">
                              {formatCurrency((Number(row.amountCents) || 0) / 100)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">
                      No missed periods. Continuing starts on the next scheduled date
                      {nextFuture ? ` (${formatChargeDate(nextFuture)})` : ''}.
                    </p>
                  )}

                  {nextFuture && (
                    <p className="text-xs text-gray-500">
                      After you continue, the next charge is {formatChargeDate(nextFuture)} — the plan keeps its usual billing day.
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-3 px-6 py-4 border-t bg-gray-50">
                  {hasOpenPeriods && (
                    <button
                      type="button"
                      disabled={!!submitting || !canBill}
                      onClick={() => runResume('bill_missed')}
                      className="w-full px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {submitting === 'bill_missed'
                        ? 'Charging…'
                        : `Continue and bill ${openTotalLabel}`}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!!submitting || !cardUsable}
                    onClick={() => runResume('continue')}
                    className={
                      hasOpenPeriods
                        ? 'w-full text-center text-sm font-medium text-slate-600 hover:text-slate-800 disabled:opacity-50'
                        : 'w-full px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50'
                    }
                  >
                    {submitting === 'continue'
                      ? 'Continuing…'
                      : hasOpenPeriods
                        ? 'Continue without billing past due periods'
                        : 'Continue plan'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
